import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, extractError } from '@/lib/api.js';
import { PageLayout } from '@/components/layout/PageLayout.js';
import { NAV_LABELS } from '@/lib/nav-labels.js';
import { TEMAS, forcaDoTema, nomeDaForca, relogio } from '@shared/conselheiro.js';

/**
 * A certificação do conselheiro (etapa 9): o painel (situação, como é a prova,
 * as tentativas) e a prova. A objetiva é corrigida na entrega; os cases, o
 * enquadramento na hora e a decisão proposta por uma pessoa da Hack.
 */

type Painel = {
  conselheiro: { status: string; certificado_em: string | null; vence_em: string | null };
  proximo_nivel: number;
  pode_iniciar: boolean;
  motivo: { codigo: string; mensagem: string; abre_em: string | null } | null;
  em_andamento: string | null;
  certificados: { codigo: string; nivel: number | null; emitido_em: string; valido_de: string | null; valido_ate: string | null }[];
  parametros: { objetivas: number; cases: number; duracao_min: number; nota_de_corte: number; intervalo_dias: number; tentativas_por_nivel: number };
  tentativas: {
    id: string;
    nivel: number;
    iniciada_em: string;
    entregue_em: string | null;
    situacao: string;
    nota_objetiva: number | null;
    resultado_em: string | null;
    sessao_em: string | null;
  }[];
};

type Prova = {
  id: string;
  nivel: number;
  situacao: string;
  iniciada_em: string;
  prazo_em: string;
  entregue_em: string | null;
  nota_objetiva: number | null;
  nota_de_corte: number;
  agora: string;
  questoes: {
    id: string;
    ordem: number;
    tipo: 'OBJETIVA' | 'CASE';
    enunciado: string;
    alternativas: string[] | null;
    resposta: { alternativa: number | null; tema: number | null; texto: string | null };
    correta: boolean | null;
    avaliacao: { aprovado: boolean; parecer: string } | null;
  }[];
};

const SITUACAO: Record<string, string> = {
  EM_ANDAMENTO: 'Em andamento',
  AGUARDANDO_AVALIACAO: 'Cases em avaliação',
  APROVADA: 'Aprovada',
  REPROVADA: 'Não aprovada',
};
const STATUS: Record<string, string> = {
  EM_FORMACAO: 'Em formação',
  CERTIFICADO: 'Certificado',
  SUSPENSO: 'Suspenso',
  DESLIGADO: 'Desligado',
};
const data = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—';
const botao = 'rounded-element px-4 py-2 text-xs font-bold uppercase text-black disabled:opacity-50';
const temaComForca = (t: number) => `${TEMAS[t]} · ${nomeDaForca(forcaDoTema(t)!)}`;

export function CertificacaoPage() {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['certificacao'],
    queryFn: async () => (await api.get<Painel>('/certificacao')).data,
  });
  const [erro, setErro] = useState<string | null>(null);
  const iniciar = useMutation({
    mutationFn: async () => (await api.post<{ id: string }>('/certificacao/tentativas')).data,
    onSuccess: (t) => navigate(`/direcao/certificacao/prova/${t.id}`),
    onError: (e) => setErro(extractError(e).message),
  });

  if (q.isLoading) return <PageLayout title={NAV_LABELS.certificacao}>Carregando…</PageLayout>;
  if (q.isError || !q.data) {
    return (
      <PageLayout title={NAV_LABELS.certificacao}>
        <p className="text-sm text-red-600">{extractError(q.error).message}</p>
      </PageLayout>
    );
  }
  const p = q.data;
  const c = p.conselheiro;
  const renovacao = p.proximo_nivel > 1;

  return (
    <PageLayout
      title={NAV_LABELS.certificacao}
      description="A prova online de certificação do conselheiro: uma parte objetiva sobre o método e cases para enquadrar e decidir."
    >
      <div className="flex flex-col gap-6">
        <section className="rounded-card border border-ha-border2 p-5">
          <p className="text-xs font-bold uppercase tracking-wide text-ha-muted">Sua situação</p>
          <p className="mt-1 text-lg font-semibold text-ha-text">
            {STATUS[c.status] ?? c.status}
            {c.status === 'CERTIFICADO' && c.vence_em ? ` · válido até ${data(c.vence_em)}` : ''}
          </p>
          {c.status === 'CERTIFICADO' ? (
            <p className="text-sm text-ha-subtle">
              A certificação vale 1 ano e se renova com a prova do nível seguinte; renovar antes do vencimento não encurta o
              ano.
            </p>
          ) : null}
        </section>

        <section className="rounded-card border border-ha-border2 p-5">
          <p className="text-xs font-bold uppercase tracking-wide text-ha-muted">
            {renovacao ? `A prova de renovação (nível ${p.proximo_nivel})` : 'A prova'}
          </p>
          <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-sm text-ha-text">
            <li>
              <strong>{p.parametros.objetivas} questões objetivas</strong> sobre o método — 5 Forças, 10 temas, a lógica
              da sequência, o protocolo de diagnóstico e as regras de vínculo. Aprovação com {p.parametros.nota_de_corte}%
              de acerto.
            </li>
            <li>
              <strong>{p.parametros.cases} cases</strong>: uma situação real de empresa (anonimizada). Você enquadra o
              problema no tema do método e escreve a decisão que levaria ao conselho. A Hack avalia por uma rubrica.
            </li>
            <li>
              <strong>{p.parametros.duracao_min} minutos</strong> do início à entrega. As respostas ficam salvas enquanto
              você responde; se o tempo acabar, a prova é entregue com o que estiver respondido.
            </li>
            <li>
              Não aprovou: nova tentativa em {p.parametros.intervalo_dias} dias, até {p.parametros.tentativas_por_nivel}{' '}
              por nível em 12 meses.
              {renovacao ? ' Na renovação, as questões e os cases são de um nível mais profundo, e nenhum case se repete.' : ''}
            </li>
          </ul>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {p.em_andamento ? (
              <Link to={`/direcao/certificacao/prova/${p.em_andamento}`} className={botao} style={{ background: 'var(--primary)' }}>
                Continuar a prova
              </Link>
            ) : p.pode_iniciar ? (
              <button
                type="button"
                className={botao}
                style={{ background: 'var(--primary)' }}
                disabled={iniciar.isPending}
                onClick={() =>
                  window.confirm(
                    `Começar a prova agora? O tempo de ${p.parametros.duracao_min} minutos começa a contar e não para.`,
                  ) && iniciar.mutate()
                }
              >
                {renovacao ? 'Começar a prova de renovação' : 'Começar a prova'}
              </button>
            ) : (
              <p className="text-sm text-ha-subtle">
                {p.motivo?.mensagem}
                {p.motivo?.abre_em ? ` Abre em ${data(p.motivo.abre_em)}.` : ''}
              </p>
            )}
            {erro ? <span className="text-sm text-red-600">{erro}</span> : null}
          </div>
        </section>

        {p.certificados.length ? (
          <section>
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ha-muted">Seus certificados</p>
            <ul className="flex flex-col divide-y divide-ha-border rounded-card border border-ha-border2">
              {p.certificados.map((x) => {
                const vigente = !x.valido_ate || Date.parse(x.valido_ate) > Date.now();
                return (
                  <li key={x.codigo} className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3 text-sm">
                    <Link to={`/certificado/${x.codigo}`} className="text-accent underline">
                      {x.nivel && x.nivel > 1 ? `${x.nivel - 1}ª renovação` : 'Certificação'} · emitido em {data(x.emitido_em)}
                    </Link>
                    <span className="text-ha-subtle">
                      {x.valido_de ? `${data(x.valido_de)} a ` : 'até '}
                      {data(x.valido_ate)} · <strong className="text-ha-text">{vigente ? 'válido' : 'vencido'}</strong>
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        {p.tentativas.length ? (
          <section>
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ha-muted">Suas tentativas</p>
            <ul className="flex flex-col divide-y divide-ha-border rounded-card border border-ha-border2">
              {p.tentativas.map((t) => (
                <li key={t.id} className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3 text-sm">
                  <Link to={`/direcao/certificacao/prova/${t.id}`} className="text-accent underline">
                    {data(t.iniciada_em)} · nível {t.nivel}
                  </Link>
                  <span className="text-ha-subtle">
                    {t.nota_objetiva != null ? `objetiva ${String(t.nota_objetiva).replace('.', ',')}% · ` : ''}
                    <strong className="text-ha-text">
                      {t.situacao === 'APROVADA' && !t.sessao_em
                        ? 'Aprovada · aguardando a sessão supervisionada'
                        : (SITUACAO[t.situacao] ?? t.situacao)}
                    </strong>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </PageLayout>
  );
}

/** O relógio do servidor, corrido no navegador. */
function useAgora(servidor: string | undefined) {
  const [agora, setAgora] = useState(() => Date.now());
  const desvio = useRef(0);
  useEffect(() => {
    if (servidor) desvio.current = Date.parse(servidor) - Date.now();
  }, [servidor]);
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return agora + desvio.current;
}

export function ProvaPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const chave = ['certificacao-prova', id];
  const q = useQuery({
    queryKey: chave,
    queryFn: async () => (await api.get<Prova>(`/certificacao/tentativas/${id}`)).data,
  });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(0);
  const agora = useAgora(q.data?.agora);

  const salvar = async (questaoId: string, corpo: Record<string, unknown>) => {
    setSalvando((n) => n + 1);
    try {
      await api.put(`/certificacao/tentativas/${id}/respostas/${questaoId}`, corpo);
      setErro(null);
    } catch (e) {
      setErro(extractError(e).message);
      void qc.invalidateQueries({ queryKey: chave });
    } finally {
      setSalvando((n) => n - 1);
    }
  };
  const entregar = useMutation({
    mutationFn: async () => (await api.post<Prova>(`/certificacao/tentativas/${id}/entregar`)).data,
    onSuccess: (p) => {
      qc.setQueryData(chave, p);
      void qc.invalidateQueries({ queryKey: ['certificacao'] });
      window.scrollTo({ top: 0 });
    },
    onError: (e) => setErro(extractError(e).message),
  });

  // Tempo esgotado com a tela aberta: entrega sozinha.
  const restante = q.data ? (Date.parse(q.data.prazo_em) - agora) / 1000 : 1;
  const emAndamento = q.data?.situacao === 'EM_ANDAMENTO';
  useEffect(() => {
    if (emAndamento && restante <= 0 && !entregar.isPending && salvando === 0) entregar.mutate();
  }, [emAndamento, restante <= 0, salvando]); // eslint-disable-line react-hooks/exhaustive-deps

  if (q.isLoading) return <PageLayout title="Prova de certificação">Carregando…</PageLayout>;
  if (q.isError || !q.data) {
    return (
      <PageLayout title="Prova de certificação">
        <p className="text-sm text-red-600">{extractError(q.error).message}</p>
      </PageLayout>
    );
  }
  const p = q.data;
  const respondidas = p.questoes.filter((x) =>
    x.tipo === 'OBJETIVA' ? x.resposta.alternativa != null : x.resposta.tema != null && !!x.resposta.texto?.trim(),
  ).length;

  return (
    <PageLayout
      title={`Prova de certificação · nível ${p.nivel}`}
      description={
        emAndamento
          ? `${respondidas} de ${p.questoes.length} respondidas · ${salvando ? 'salvando…' : 'respostas salvas'}`
          : `Entregue em ${data(p.entregue_em)}`
      }
      actions={
        <Link to="/direcao/certificacao" className="text-sm text-accent underline">
          ← Certificação
        </Link>
      }
    >
      {emAndamento ? (
        <div className="sticky top-0 z-10 mb-4 flex items-center justify-between gap-3 rounded-card border border-ha-border2 bg-ha-card p-3">
          <span className={`text-2xl font-semibold tabular-nums ${restante < 600 ? 'text-red-600' : 'text-ha-text'}`}>
            {relogio(restante)}
          </span>
          <button
            type="button"
            className={botao}
            style={{ background: 'var(--primary)' }}
            disabled={entregar.isPending || salvando > 0}
            onClick={() =>
              window.confirm(
                respondidas < p.questoes.length
                  ? `Faltam ${p.questoes.length - respondidas} questões. Entregar assim mesmo? Depois de entregue, não muda mais.`
                  : 'Entregar a prova? Depois de entregue, não muda mais.',
              ) && entregar.mutate()
            }
          >
            Entregar a prova
          </button>
        </div>
      ) : (
        <Resultado p={p} />
      )}
      {erro ? <p className="mb-4 text-sm text-red-600">{erro}</p> : null}

      <ol className="flex flex-col gap-4">
        {p.questoes.map((x) => (
          <li key={x.id} className="rounded-card border border-ha-border2 p-4">
            <p className="mb-1 text-xs font-bold uppercase tracking-wide text-ha-muted">
              {x.ordem}. {x.tipo === 'OBJETIVA' ? 'Objetiva' : 'Case'}
              {!emAndamento && x.correta != null ? (
                <span className={x.correta ? 'ml-2 text-emerald-700' : 'ml-2 text-red-600'}>
                  {x.tipo === 'OBJETIVA' ? (x.correta ? '✓ certa' : '✗ errada') : x.correta ? '✓ enquadramento certo' : '✗ enquadramento diferente do esperado'}
                </span>
              ) : null}
            </p>
            <p className="mb-3 whitespace-pre-wrap text-sm text-ha-text">{x.enunciado}</p>
            {x.tipo === 'OBJETIVA' ? (
              <Objetiva q={x} editavel={emAndamento} salvar={salvar} />
            ) : (
              <Case q={x} editavel={emAndamento} salvar={salvar} />
            )}
            {x.avaliacao ? (
              <div className="mt-3 rounded-element border border-ha-border2 p-3 text-sm">
                <p className="font-semibold text-ha-text">{x.avaliacao.aprovado ? 'Case aprovado' : 'Case não aprovado'}</p>
                <p className="whitespace-pre-wrap text-ha-subtle">{x.avaliacao.parecer}</p>
              </div>
            ) : null}
          </li>
        ))}
      </ol>
    </PageLayout>
  );
}

function Resultado({ p }: { p: Prova }) {
  const passou = (p.nota_objetiva ?? 0) >= p.nota_de_corte;
  return (
    <div className="mb-4 rounded-card border-2 p-4" style={{ borderColor: p.situacao === 'APROVADA' ? 'var(--primary)' : 'var(--border)' }}>
      <p className="text-lg font-semibold text-ha-text">{SITUACAO[p.situacao] ?? p.situacao}</p>
      <p className="text-sm text-ha-subtle">
        Objetiva: {String(p.nota_objetiva ?? 0).replace('.', ',')}% de acerto ({passou ? 'acima' : 'abaixo'} dos{' '}
        {p.nota_de_corte}% exigidos).
        {p.situacao === 'AGUARDANDO_AVALIACAO' ? ' Os cases estão com a equipe da Hack para avaliação; você recebe o resultado por e-mail.' : ''}
        {p.situacao === 'REPROVADA' && !passou ? ' Com a objetiva abaixo do corte, os cases não chegam a ser avaliados.' : ''}
        {p.situacao === 'APROVADA' ? ' O próximo passo é a sessão supervisionada, que a equipe da Hack combina com você.' : ''}
      </p>
    </div>
  );
}

type Salvar = (questaoId: string, corpo: Record<string, unknown>) => Promise<void>;

function Objetiva({ q, editavel, salvar }: { q: Prova['questoes'][number]; editavel: boolean; salvar: Salvar }) {
  const [marcada, setMarcada] = useState(q.resposta.alternativa);
  return (
    <div className="flex flex-col gap-2">
      {(q.alternativas ?? []).map((a, i) => (
        <label key={i} className="flex items-start gap-2 text-sm text-ha-text">
          <input
            type="radio"
            name={q.id}
            className="mt-1"
            checked={marcada === i}
            disabled={!editavel}
            onChange={() => {
              setMarcada(i);
              void salvar(q.id, { alternativa: i });
            }}
          />
          <span>{a}</span>
        </label>
      ))}
    </div>
  );
}

function Case({ q, editavel, salvar }: { q: Prova['questoes'][number]; editavel: boolean; salvar: Salvar }) {
  const [tema, setTema] = useState(q.resposta.tema);
  const [texto, setTexto] = useState(q.resposta.texto ?? '');
  const salvo = useRef(q.resposta.texto ?? '');
  // O texto se salva sozinho depois de uma pausa na digitação, e ao sair do campo.
  useEffect(() => {
    if (!editavel || texto === salvo.current) return;
    const t = setTimeout(() => {
      salvo.current = texto;
      void salvar(q.id, { texto });
    }, 1500);
    return () => clearTimeout(t);
  }, [texto, editavel]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex flex-col gap-3">
      <label className="text-xs font-semibold uppercase tracking-wide text-ha-muted">
        Em que tema do método este problema se enquadra?
        <select
          className="mt-1 block w-full max-w-sm rounded-element border border-ha-border2 bg-transparent px-2 py-1.5 text-sm normal-case tracking-normal text-ha-text"
          value={tema ?? ''}
          disabled={!editavel}
          onChange={(e) => {
            const v = e.target.value === '' ? null : Number(e.target.value);
            setTema(v);
            void salvar(q.id, { tema: v });
          }}
        >
          <option value="">Escolha o tema</option>
          {TEMAS.map((_, i) => (
            <option key={i} value={i}>
              {temaComForca(i)}
            </option>
          ))}
        </select>
      </label>
      <label className="text-xs font-semibold uppercase tracking-wide text-ha-muted">
        A decisão que você levaria ao conselho, e por quê
        <textarea
          className="mt-1 block w-full rounded-element border border-ha-border2 bg-transparent px-3 py-2 text-sm normal-case tracking-normal text-ha-text"
          rows={8}
          value={texto}
          disabled={!editavel}
          placeholder="A decisão, quem responde por ela, o prazo e o sinal de que deu certo."
          onChange={(e) => setTexto(e.target.value)}
          onBlur={() => {
            if (editavel && texto !== salvo.current) {
              salvo.current = texto;
              void salvar(q.id, { texto });
            }
          }}
        />
      </label>
    </div>
  );
}
